/**
 * Inbox thread direction / unread flags — mirrors server/utils/messageUnreadCount.js.
 * Used for sidebar filters and real-time socket card updates.
 */

function isInboxAnalyzedChatType(msg) {
  return (
    msg &&
    msg.type !== "assignment" &&
    msg.type !== "date" &&
    msg.type !== "closechat" &&
    msg.type !== "blockcontact"
  );
}

export function isIncomingChatMessage(msg, contact) {
  if (!msg || !contact || !isInboxAnalyzedChatType(msg)) return false;

  const platform = contact.platform || "whatsapp";
  const hasClientId = msg.clientid && !msg.senderid;
  const phoneNumberID = contact.phoneNumberID;
  const IGid = contact.IGid;
  const pageScopedId = contact.pageScopedId;
  const fromMatchesContact =
    platform === "instagram"
      ? msg.from === IGid || msg.from === String(IGid)
      : platform === "facebook"
        ? msg.from === pageScopedId || msg.from === String(pageScopedId)
        : msg.from === phoneNumberID;
  return !!(hasClientId || fromMatchesContact);
}

export function isIncomingChatMessageUnread(msg) {
  if (!msg || !isInboxAnalyzedChatType(msg)) return false;
  return (
    (msg.status === "received" || msg.status === "delivered") &&
    msg.status !== "read" &&
    msg.status !== "seen"
  );
}

export function findInboxContactInData(inboxDataRoot, contactId) {
  if (!inboxDataRoot || contactId == null || contactId === "") return null;
  const id = String(contactId);
  for (const category of Object.keys(inboxDataRoot)) {
    const categoryData = inboxDataRoot[category];
    if (!Array.isArray(categoryData)) continue;
    for (const item of categoryData) {
      if (!item) continue;
      const topId = item.contactId || item.contactID;
      if (topId && String(topId) === id) return item;
      if (Array.isArray(item.data)) {
        const nested = item.data.find(
          (c) => c && String(c.contactId || c.contactID) === id
        );
        if (nested) return nested;
      }
    }
  }
  return null;
}

export function buildContactLikeForSocket(existingInboxContact, normalizedMessage) {
  const platform =
    normalizedMessage?.platform ?? existingInboxContact?.platform ?? "whatsapp";
  const listPhone =
    existingInboxContact?.phoneNumber ||
    existingInboxContact?.phoneNumberID ||
    null;
  const normPhone =
    normalizedMessage?.phoneNumberID || normalizedMessage?.phoneNumber || null;

  return {
    platform,
    phoneNumberID:
      (platform === "whatsapp" || platform === "qr")
        ? existingInboxContact?.phoneNumberID ||
          listPhone ||
          normPhone ||
          normalizedMessage?.phoneNumberID
        : existingInboxContact?.phoneNumberID ||
          normalizedMessage?.phoneNumberID ||
          null,
    IGid:
      platform === "instagram"
        ? existingInboxContact?.IGid ||
          normalizedMessage?.IGid ||
          listPhone ||
          normPhone
        : existingInboxContact?.IGid || normalizedMessage?.IGid || null,
    pageScopedId:
      platform === "facebook"
        ? existingInboxContact?.pageScopedId ||
          normalizedMessage?.pageScopedId ||
          listPhone ||
          normPhone
        : existingInboxContact?.pageScopedId ||
          normalizedMessage?.pageScopedId ||
          null,
  };
}

export function buildContactLikeFromInboxCard(contact) {
  if (!contact) {
    return { platform: "whatsapp", phoneNumberID: null, IGid: null, pageScopedId: null };
  }
  const platform = contact.platform || "whatsapp";
  const phone =
    contact.phoneNumber || contact.phoneNumberID || null;
  return {
    platform,
    phoneNumberID:
      (platform === "whatsapp" || platform === "qr")
        ? contact.phoneNumberID || phone
        : contact.phoneNumberID || null,
    IGid:
      platform === "instagram"
        ? contact.IGid || phone
        : contact.IGid || null,
    pageScopedId:
      platform === "facebook"
        ? contact.pageScopedId || phone
        : contact.pageScopedId || null,
  };
}

/** When API did not send lastMessageFromContact / lastMessageUnreadByTeam (older cache). */
export function legacyLastMessageStubFromInboxContact(contact) {
  const slm = contact.senderLastMessage;
  if (!slm || !contact.lastMessage) return null;
  const assumedUnread = (contact.receivedCount || 0) > 0;
  return {
    type: "chat",
    from: slm.phoneNumber,
    senderid: slm.senderid || null,
    status: assumedUnread ? "received" : "read",
  };
}

export function computeLastMessageThreadFlagsFromChat(messageChat, contactLike) {
  if (!messageChat || !contactLike) {
    return { lastMessageFromContact: false, lastMessageUnreadByTeam: false };
  }
  const lastMessageFromContact = isIncomingChatMessage(messageChat, contactLike);
  const lastMessageUnreadByTeam =
    lastMessageFromContact && isIncomingChatMessageUnread(messageChat);
  return { lastMessageFromContact, lastMessageUnreadByTeam };
}

export function inboxContactMatchesUnreadFilter(contact) {
  if (!contact) return false;
  if (
    typeof contact.lastMessageFromContact === "boolean" &&
    typeof contact.lastMessageUnreadByTeam === "boolean"
  ) {
    return contact.lastMessageFromContact && contact.lastMessageUnreadByTeam;
  }
  const stub = legacyLastMessageStubFromInboxContact(contact);
  if (!stub) return false;
  return isIncomingChatMessage(stub, buildContactLikeFromInboxCard(contact)) &&
    isIncomingChatMessageUnread(stub);
}

export function inboxContactMatchesUnrepliedFilter(contact) {
  if (!contact) return false;
  // Bug fix (2026-09-01, Rahim: a brand-new UNREAD customer message didn't count as
  // "Unreplied" — only ones already opened/read but not yet answered did). "Unreplied"
  // means the customer is waiting on a reply, full stop; whether their message has been
  // opened yet doesn't change that. Dropped the `&& !lastMessageUnreadByTeam` restriction
  // that made this a narrower "read but not answered" state — Unreplied is now simply
  // "the last message came from the contact," which also makes it a proper superset of
  // the separate Unread filter (inboxContactMatchesUnreadFilter, InboxAll's "Show"
  // dropdown) rather than deliberately excluding what that one already covers.
  if (typeof contact.lastMessageFromContact === "boolean") {
    return contact.lastMessageFromContact;
  }
  const stub = legacyLastMessageStubFromInboxContact(contact);
  if (!stub) return false;
  return isIncomingChatMessage(stub, buildContactLikeFromInboxCard(contact));
}
